import type { Update } from '@/types/update'
import type { CalendarEvent, CreateAbsenceRequest, UpdateAbsenceRequest, ListEventsParams } from '@/types/calendar'
import { useAuthStore } from '@/stores/auth.store'
import { API_BASE_URL } from './api-config'

const API_URL = API_BASE_URL

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

async function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const accessToken = useAuthStore.getState().accessToken

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
    credentials: 'include',
  })

  const data = await response.json()

  if (!response.ok) {
    throw new ApiError(response.status, data.error || 'Request failed')
  }

  return data
}

export interface ParentChild {
  id: string
  firstName: string
  lastName: string
  dateOfBirth: string | null
  relationship: string
}

export interface UpdatesListParams {
  childId?: string
  type?: string
  before?: string  // Cursor for loading older updates
  since?: string   // Filter updates since this date
  limit?: number
}

export interface UpdatesListResponse {
  updates: Update[]
  hasMore: boolean
  nextCursor: string | null
}

export const parentApi = {
  async getChildren(): Promise<{ children: ParentChild[] }> {
    return fetchWithAuth<{ children: ParentChild[] }>('/api/parent/children')
  },

  async getChild(id: string): Promise<{ child: ParentChild }> {
    return fetchWithAuth<{ child: ParentChild }>(`/api/parent/children/${id}`)
  },

  async getUpdates(params?: UpdatesListParams): Promise<UpdatesListResponse> {
    const searchParams = new URLSearchParams()
    if (params?.childId) searchParams.set('childId', params.childId)
    if (params?.type) searchParams.set('type', params.type)
    if (params?.before) searchParams.set('before', params.before)
    if (params?.since) searchParams.set('since', params.since)
    if (params?.limit) searchParams.set('limit', params.limit.toString())

    const query = searchParams.toString()
    return fetchWithAuth<UpdatesListResponse>(`/api/parent/updates${query ? `?${query}` : ''}`)
  },

  // Calendar events
  async getCalendarEvents(params?: ListEventsParams): Promise<{ events: CalendarEvent[] }> {
    const searchParams = new URLSearchParams()
    if (params?.startDate) searchParams.set('startDate', params.startDate)
    if (params?.endDate) searchParams.set('endDate', params.endDate)
    if (params?.childId) searchParams.set('childId', params.childId)

    const query = searchParams.toString()
    return fetchWithAuth<{ events: CalendarEvent[] }>(`/api/parent/calendar/events${query ? `?${query}` : ''}`)
  },

  async createAbsence(data: CreateAbsenceRequest): Promise<{ event: CalendarEvent }> {
    return fetchWithAuth<{ event: CalendarEvent }>('/api/parent/calendar/events', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async updateAbsence(id: string, data: UpdateAbsenceRequest): Promise<{ event: CalendarEvent }> {
    return fetchWithAuth<{ event: CalendarEvent }>(`/api/parent/calendar/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async deleteAbsence(id: string): Promise<{ success: boolean }> {
    return fetchWithAuth<{ success: boolean }>(`/api/parent/calendar/events/${id}`, {
      method: 'DELETE',
    })
  },
}

export { ApiError }
